import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import api from '../api';
import { SocialProvider } from '../types';
import { signupSchema, SignupFormData } from '../schemas/auth';
import { useAppDispatch } from '../store/hooks';
import { loginUser } from '../store/slices/userSlice';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import TermsOfServiceModal from '../components/TermsOfServiceModal';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      rememberMe: true,
      agreeToPrivacy: false,
    },
    mode: 'onBlur',
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    setSubmitError('');

    try {
      const response = await api.auth.register({
        email: data.email,
        password: data.password,
        name: data.name
      });

      // Handle Remember Me logic (same as login)
      localStorage.setItem('login_remember_me', String(data.rememberMe));
      if (data.rememberMe) {
        localStorage.setItem('remembered_email', data.email);
      } else {
        localStorage.removeItem('remembered_email');
      }

      // Dispatch login action to Redux (handles token and user storage)
      await dispatch(loginUser({
        user: response.user,
        accessToken: response.accessToken,
        rememberMe: data.rememberMe
      })).unwrap();

      // Navigate to welcome screen on success
      navigate('/welcome');
    } catch (error: any) {
      setSubmitError(
        error.response?.data?.message || error.message || 'Registration failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignup = (provider: SocialProvider) => {
    if (provider === SocialProvider.GOOGLE) {
      api.auth.google();
    } else if (provider === SocialProvider.TWITTER) {
      api.auth.twitter();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-white flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden">
      {/* Enhanced orange gradient background animations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-400/20 via-orange-300/10 to-transparent rounded-full animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-orange-500/15 via-orange-400/10 to-transparent rounded-full animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }}></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(251, 146, 60, 0.1) 0%, rgba(254, 243, 199, 0.05) 50%, transparent 100%)'
          }}
        ></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6 group">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:shadow-xl group-hover:shadow-orange-500/40 transition-all duration-300">
              <span className="text-white font-bold text-lg sm:text-xl">F</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">Focuzon</h1>
          </Link>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Create Account</h2>
          <p className="text-sm sm:text-base text-gray-600">Sign up to start your focus journey</p>
        </div>

        {/* Signup Form */}
        {/* Signup Form */}
        <div className="bg-white/95 backdrop-blur-sm border-2 border-orange-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-2xl shadow-orange-500/10">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                {...register('name')}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg sm:rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm sm:text-base ${errors.name ? 'border-red-400 focus:ring-red-400' : 'border-orange-200'
                  }`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-400 font-semibold">* {errors.name.message}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                {...register('email')}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg sm:rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm sm:text-base ${errors.email ? 'border-red-400 focus:ring-red-400' : 'border-orange-200'
                  }`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400 font-semibold">* {errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  {...register('password')}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 bg-white border-2 rounded-lg sm:rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm sm:text-base ${errors.password ? 'border-red-400 focus:ring-red-400' : 'border-orange-200'
                    }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange-600 transition-colors focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-400 font-semibold">* {errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  {...register('confirmPassword')}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 bg-white border-2 rounded-lg sm:rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-sm sm:text-base ${errors.confirmPassword ? 'border-red-400 focus:ring-red-400' : 'border-orange-200'
                    }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange-600 transition-colors focus:outline-none"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-400 font-semibold">* {errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center">
              <label className="flex items-center space-x-2 sm:space-x-3">
                <input
                  type="checkbox"
                  {...register('rememberMe')}
                  className="w-4 h-4 text-[#FF5924] bg-white border-[#E5E5E5] rounded focus:ring-0 focus:outline-none accent-[#FF5924]"
                />
                <span className="text-gray-700 text-xs sm:text-sm">Remember me</span>
              </label>
            </div>

            {/* Privacy Policy Checkbox */}
            <div>
              <label className="flex items-start space-x-2 sm:space-x-3">
                <input
                  type="checkbox"
                  {...register('agreeToPrivacy')}
                  className={`w-4 h-4 text-[#FF5924] bg-white border-[#E5E5E5] rounded focus:ring-0 focus:outline-none mt-0.5 sm:mt-1 accent-[#FF5924] ${errors.agreeToPrivacy ? 'border-red-500' : ''
                    }`}
                />
                <div className="text-xs sm:text-sm text-[#6B6B6B]">
                  <span>I agree to the </span>
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-[#FF5924] hover:text-[#E54A1F] transition-colors underline"
                  >
                    Privacy Policy
                  </button>
                  <span> and </span>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-[#FF5924] hover:text-[#E54A1F] transition-colors underline"
                  >
                    Terms of Service
                  </button>
                </div>
              </label>
              {errors.agreeToPrivacy && (
                <p className="mt-3 text-xs text-red-400 font-semibold">* {errors.agreeToPrivacy.message}</p>
              )}
            </div>

            {/* Submit Button */}
            {submitError && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg sm:rounded-xl p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-red-600">{submitError}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base transform hover:scale-[1.02] active:scale-[0.98]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing up...</span>
                </div>
              ) : (
                'Sign Up'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-4 sm:my-6 flex items-center">
            <div className="flex-1 border-t-2 border-orange-200"></div>
            <span className="px-3 sm:px-4 text-gray-500 text-xs sm:text-sm font-medium">or</span>
            <div className="flex-1 border-t-2 border-orange-200"></div>
          </div>

          {/* Social Signup */}
          <div className="space-y-2 sm:space-y-3">
            <button
              type="button"
              onClick={() => handleSocialSignup(SocialProvider.GOOGLE)}
              className="w-full bg-white border-2 border-orange-200 hover:border-orange-400 text-gray-800 hover:text-orange-600 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>Continue with Google</span>
            </button>
            {/* <button
              type="button"
              onClick={() => handleSocialSignup(SocialProvider.TWITTER)}
              className="w-full bg-white border-2 border-orange-200 hover:border-orange-400 text-gray-800 hover:text-orange-600 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
              </svg>
              <span>Continue with X (Twitter)</span>
            </button> */}
          </div>

          {/* Sign In Link */}
          <div className="mt-6 sm:mt-8 text-center">
            <p className="text-xs sm:text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-orange-600 hover:text-orange-700 font-semibold transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4 sm:mt-6">
          <Link
            to="/"
            className="text-gray-600 hover:text-orange-600 transition-colors text-xs sm:text-sm font-medium inline-flex items-center space-x-1"
          >
            <span>←</span>
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};

export default SignupPage;

